import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import {
    Background,
    CFlex,
    CMargin,
    DText,
    Flex,
    Padding
} from '../../../../styles/CommonStyles'
import Text from '../../../../core/utils/Text'
import CoinSelect from '../../../common/input/CoinSelect'
import Input from '../../../common/input/Input'
import ActionButton from '../../../common/buttons/ActionButton'
import ModalLayout from '../../../layout/main/ModalLayout'
import Select from '../../../common/dropdown/Select'
import { deepCopy, formatNumber, stringToNumber } from '../../../../core/utils/common'
import { SOCKET_URL } from '../../../../core/constants/urls'
import { useQueryContext } from '../../../../core/contexts/query'
import { useSetFixerSettings } from '../../../../core/services/react-query/setting/fixer'

const initialEditableFixer = {
    feeFactorBuy: '',
    feeFactorSell: '',
    isActive: true
}

const ACTIVE_OPTIONS = ['active', 'inactive']

const FixerSettings = ({ fixerSettings }) => {

    const { setToast } = useQueryContext()
    const [showError, setShowError] = useState(false)

    const [coin, setCoin] = useState(null)
    const [editableFixer, setEditableFixer] = useState(initialEditableFixer)

    const [modal, setModal] = useState({ open: false, type: null })
    const openModal = (type) => setModal({ open: true, type })
    const closeModal = () => setModal({ open: false, type: null })

    const { mutate, isLoading } = useSetFixerSettings(closeModal)

    const fixerAdded = useMemo(() => {
        setShowError(false)

        if (coin && fixerSettings?.length) {
            const found = fixerSettings.find(x => x.currency === coin.id)
            if (found) {
                setEditableFixer(found)
                return found
            }
        }

        setEditableFixer(initialEditableFixer)
        return null
    }, [coin, fixerSettings])

    const onSubmit = () => {
        if (!editableFixer.feeFactorBuy || !editableFixer.feeFactorSell) {
            setShowError(true)
            setToast({ message: 'fill-inputs-error', show: true, isError: true })
            return
        }

        const newList = deepCopy(fixerSettings || [])

        const obj = {
            currency: coin.id,
            feeFactorBuy: stringToNumber(editableFixer.feeFactorBuy),
            feeFactorSell: stringToNumber(editableFixer.feeFactorSell),
            isActive: editableFixer.isActive
        }

        if (modal.type === 'add') {
            newList.push(obj)
        } else {
            const index = newList.findIndex(x => x.currency === coin.id)
            if (index > -1) newList[index] = obj
        }

        mutate(newList)
    }

    return (
        <>
            <Padding padding={'30px 10px'}>
                <CFlex style={{ gap: 20 }}>
                    <DText primary>
                        <Text tid={'fixer-setting-title'} />
                    </DText>

                    <CoinSelect
                        width={'300px'}
                        value={coin}
                        onChange={setCoin}
                    />

                    {!!fixerAdded ? (
                        <>
                            <CFlex align={'flex-start'}>
                                <DText main fontSize={'s'}>
                                    <Text tid={'feeFactorBuy'} />
                                </DText>
                                <TSItem>
                                    {formatNumber(fixerAdded.feeFactorBuy)}
                                </TSItem>
                            </CFlex>

                            <CFlex align={'flex-start'}>
                                <DText main fontSize={'s'}>
                                    <Text tid={'feeFactorSell'} />
                                </DText>
                                <TSItem>
                                    {formatNumber(fixerAdded.feeFactorSell)}
                                </TSItem>
                            </CFlex>

                            <CFlex align={'flex-start'}>
                                <DText main fontSize={'s'}>
                                    <Text tid={'status'} />
                                </DText>
                                <TSItem>
                                    {fixerAdded.isActive ? 'Active' : 'Inactive'}
                                </TSItem>
                            </CFlex>

                            <ActionButton
                                active
                                width={'300px'}
                                height={'40px'}
                                onClick={() => openModal('edit')}
                            >
                                <Text tid={'edit-fixer-setting'} />
                            </ActionButton>
                        </>
                    ) : (
                        <>
                            <DText main>
                                <Text tid={'fixer-setting-not-available'} />
                            </DText>
                            <ActionButton
                                active
                                width={'300px'}
                                height={'40px'}
                                onClick={() => openModal('add')}
                            >
                                <Text tid={'add-fixer-setting'} />
                            </ActionButton>
                        </>
                    )}
                </CFlex>
            </Padding>

            <ModalLayout open={modal.open} width={'480px'} isStatic>
                <CFlex style={{ gap: 12 }}>
                    <DText primary>
                        <Text tid={`fixer-title-${modal.type}`} />
                    </DText>

                    <Flex>
                        <img
                            src={
                                coin?.id === 'irt'
                                    ? require('../../../../assets/images/tooman.png')
                                    : SOCKET_URL + `assets/icon/${coin?.id}.png`
                            }
                            alt={' '}
                            width={'32px'}
                        />
                        <DText main style={{ margin: '0 8px' }}>
                            {coin?.id?.toUpperCase()}
                        </DText>
                    </Flex>

                    <Background bg={'mainBg'}>
                        <Input
                            size={'small'}
                            value={editableFixer.feeFactorBuy}
                            onInputChange={(v) =>
                                setEditableFixer(s => ({ ...s, feeFactorBuy: v }))
                            }
                            label={'feeFactorBuy'}
                            error={showError ? 'required' : ''}
                            number
                        />

                        <Input
                            size={'small'}
                            value={editableFixer.feeFactorSell}
                            onInputChange={(v) =>
                                setEditableFixer(s => ({ ...s, feeFactorSell: v }))
                            }
                            label={'feeFactorSell'}
                            error={showError ? 'required' : ''}
                            number
                        />

                        <Select
                            options={ACTIVE_OPTIONS}
                            value={editableFixer.isActive ? 'active' : 'inactive'}
                            onValueChange={(idx) =>
                                setEditableFixer(s => ({ ...s, isActive: idx === 0 }))
                            }
                            size={'small'}
                            width={'100%'}
                            height={'40px'}
                        />
                    </Background>

                    <Flex fw style={{ gap: 20 }}>
                        <ActionButton
                            active
                            loading={isLoading}
                            width={'45%'}
                            height={'40px'}
                            onClick={onSubmit}
                        >
                            <Text tid={'submit'} />
                        </ActionButton>
                        <ActionButton
                            width={'45%'}
                            height={'40px'}
                            onClick={closeModal}
                        >
                            <Text tid={'cancel'} />
                        </ActionButton>
                    </Flex>
                </CFlex>
            </ModalLayout>
        </>
    )
}

const TSItem = styled.div`
  color: ${p => p.theme.color};
  direction: ltr;
  border-radius: 6px;
  border: 1px solid ${p => p.theme.color}10;
  background-color: ${p => p.theme.secondaryBg};
  padding: 8px 16px;
  width: 300px;
  height: 40px;
`

export default FixerSettings
